import axios, { AxiosError } from 'axios';
import type { ApiError } from '../types';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000';
const AUTH_BASE_URL = (import.meta as any).env?.VITE_AUTH_SERVICE_URL || API_BASE_URL;

// Dynamically resolve Billing and Analytics URLs in development if API_BASE_URL is pointing directly to port 3002
const getBillingBaseUrl = () => {
  const envUrl = (import.meta as any).env?.VITE_BILLING_SERVICE_URL;
  if (envUrl) return envUrl;
  if (API_BASE_URL.includes(':3002')) {
    return API_BASE_URL.replace(':3002', ':3003');
  }
  return API_BASE_URL;
};

const getAnalyticsBaseUrl = () => {
  const envUrl = (import.meta as any).env?.VITE_ANALYTICS_SERVICE_URL;
  if (envUrl) return envUrl;
  if (API_BASE_URL.includes(':3002')) {
    return API_BASE_URL.replace(':3002', ':3006');
  }
  return API_BASE_URL;
};

const BILLING_BASE_URL = getBillingBaseUrl();
const ANALYTICS_BASE_URL = getAnalyticsBaseUrl();

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and proper path prefixes
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  const user = useAuthStore.getState().user;
  const client = useAuthStore.getState().client;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add client ID header for tenant isolation
  if (client?.id) {
    config.headers['x-client-id'] = client.id;
  }

  // Add user context headers for gatewayAuthMiddleware when not injected by Kong
  if (user) {
    if (user.id) config.headers['x-user-id'] = user.id;
    if (user.role) config.headers['x-user-role'] = user.role;
    if (user.email) config.headers['x-user-email'] = user.email;
  }

  // Add proper prefix based on endpoint
  if (config.url) {
    // Auth endpoints - no prefix needed
    if (config.url.startsWith('/auth/')) {
      config.baseURL = AUTH_BASE_URL;
      return config;
    }

    // Wallet and invoice endpoints - use /billing prefix
    if (
      config.url.startsWith('/wallet') ||
      config.url.startsWith('/invoices') ||
      config.url.startsWith('/transactions') ||
      config.url.startsWith('/billing/') ||
      config.url === '/billing'
    ) {
      if (!config.url.startsWith('/billing/') && config.url !== '/billing') {
        config.url = `/billing${config.url}`;
      }
      config.baseURL = BILLING_BASE_URL;
      return config;
    }

    // Analytics endpoints - use /analytics prefix
    if (config.url.startsWith('/analytics')) {
      config.baseURL = ANALYTICS_BASE_URL;
      return config;
    }

    // Support tickets - map /tickets to /api/support-tickets
    if (config.url.startsWith('/tickets')) {
      config.url = config.url.replace('/tickets', '/api/support-tickets');
      return config;
    }

    // Payment methods endpoint - route to admin products payment methods store
    if (config.url === '/payment-methods') {
      config.url = '/portal/api/portal/admin/products/payment-methods/list';
      return config;
    }

    // All other endpoints - use /api prefix (core-api-service)
    if (!config.url.startsWith('/api/')) {
      config.url = `/api${config.url}`;
    }
  }

  return config;
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && originalRequest && !originalRequest.headers['X-Retry']) {
      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(`${AUTH_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const payload = response.data?.data || response.data;
          const { token, refresh_token } = payload;

          // Update tokens in Zustand store
          useAuthStore.getState().updateTokens(token, refresh_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          originalRequest.headers['X-Retry'] = 'true';
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect to login
          useAuthStore.getState().clearAuth();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    return apiError?.error?.message || error.message || 'An unexpected error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
